// GoogleCalendarIntegration.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { connectApp } from '../api/Api'; // must return { method:'oauth', auth_url, state, expires_at }

import NotificationContext from '../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../components/commonComponents/gxAntdToast/toastUtils';

function openCenteredPopup(url, { width = 500, height = 600 } = {}) {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

  const w = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const h = window.innerHeight || document.documentElement.clientHeight || window.screen.height;

  const systemZoom = w / window.screen.availWidth;
  const left = (w - width) / 2 / systemZoom + dualScreenLeft;
  const top = (h - height) / 2 / systemZoom + dualScreenTop;

  const features = [
    `width=${width}`,
    `height=${height}`,
    `top=${top}`,
    `left=${left}`,
    'status=no',
    'resizable=yes',
    'toolbar=no',
    'menubar=no',
    'scrollbars=yes',
  ].join(',');

  const popup = window.open(url, 'oauth_popup', features);
  if (!popup) throw new Error('Popup blocked. Please allow popups and try again.');
  popup.focus();
  return popup;
}

/**
 * Wait for our relay page to post back the final result AFTER backend finishes OAuth.
 * We only accept same-origin messages and provider === 'google'.
 * Expected message shape (from relay page inside popup after BE completes):
 *   { provider:'google', status:'success'|'error', code:'google_calendar', post_connect_next_url, message }
 */
function waitForBackendResult({ timeoutMs = 120000 }) {
  return new Promise((resolve, reject) => {
    const origin = window.location.origin;
    let done = false;

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      reject(new Error('Authentication timed out'));
    }, timeoutMs);

    function onMessage(event) {
      if (event.origin !== origin) return; // same-origin only
      const data = event.data || {};
      if (data.provider !== 'google') return;

      if (done) return;
      done = true;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);

      resolve({
        status: data.status,
        appCode: data.code, // e.g., 'google_calendar'
        postConnectNextUrl: data.post_connect_next_url,
        message: data.message,
      });
    }

    window.addEventListener('message', onMessage);
  });
}

const GoogleCalendarIntegration = ({
  app,                // { code, name, icon, category, description, features, connected }
  isOpen,
  onClose,
  onConnectionSuccess, // <- call this to trigger your "GET /api/v1/integrations?brand_id=..." refresh
  onContinueSetup,     // optional: navigate to post_connect_next_url
  toast,               // optional: your toast function { success, error, info }
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connected' | 'disconnected' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const { currentBrand } = useSelector((state) => state?.app);
  const notification = useContext(NotificationContext);

  useEffect(() => {
    if (app) setConnectionStatus(app.connected ? 'connected' : 'disconnected');
  }, [app]);

  // This allows BE to redirect the MAIN window too (non-popup fallback),
  // e.g., /integrations?status=success&code=google_calendar&post_connect_next_url=...
  // If a user somehow lands with those query params, we still update UI.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const code = params.get('code');
    const nextUrl = params.get('post_connect_next_url');
    if (status && code) {
      if (status === 'success') {
        setConnectionStatus('connected');
        showSuccessToast(notification, 'Google Calendar connected successfully');
        onConnectionSuccess?.(); // refresh list
        if (nextUrl && onContinueSetup) onContinueSetup(nextUrl);
      } else {
        setConnectionStatus('error');
        setErrorMessage('Authorization failed');
        showErrorToast(notification, 'Authorization failed');
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [onConnectionSuccess, onContinueSetup, notification]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setErrorMessage('');
    try {
      // FE -> BE (Start)
      const returnTo = `${window.location.origin}/user/integrations/oauth-callback`; // FIXED: point to relay page
      const payload = {
        account_id: currentBrand?.account_id, // include if you have it
        brand_id: currentBrand?.brand_id,
        scope: 'individual',
        return_to: returnTo,
      };

      const start = await connectApp(app.code, payload);
      if (!start || start.method !== 'oauth' || !start.auth_url) {
        throw new Error(start?.response?.data?.message || start?.response?.data?.error || 'Invalid OAuth start response');
      }

      const popup = openCenteredPopup(start.auth_url, { width: 500, height: 600 });

      // Wait for backend-completed result via relay page
      const { status, postConnectNextUrl, message } = await waitForBackendResult({
        timeoutMs: 120000,
      });

      if (status === 'success') {
        if (popup && !popup.closed) {
          setTimeout(() => {
            try {
              if (!popup.closed) popup.close();
            } catch (err) {
              console.warn('Failed to close popup after delay', err);
            }
          }, 3500); // give relay page countdown time to show completion UI
        }

        setConnectionStatus('connected');
        showSuccessToast(notification, 'Google Calendar connected successfully');
        onConnectionSuccess?.(); // triggers GET /api/v1/integrations?brand_id=...
        
        // FIXED: Close the modal after success
        setTimeout(() => {
          onClose?.();
        }, 500); // Small delay to show success message
        
        // optional CTA
        if (postConnectNextUrl && onContinueSetup) onContinueSetup(postConnectNextUrl);
      } else {
        setConnectionStatus('error');
        const msg = message || 'Authorization failed';
        setErrorMessage(msg);
        showErrorToast(notification, msg);
      }
    } catch (err) {
      setConnectionStatus('error');
      showErrorToast(notification, err.message || err.error?.message || err.error || 'Failed to initiate connection');
      setErrorMessage(err.message || 'Failed to initiate connection');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      // await disconnectIntegration(app.id)
      setConnectionStatus('disconnected');
      showSuccessToast(notification, 'Google Calendar disconnected');
      onConnectionSuccess?.();
      onClose?.();
    } catch (err) {
      setErrorMessage('Failed to disconnect');
      showErrorToast(notification, 'Failed to disconnect');
    }
  };

  if (!isOpen || !app) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              {app.icon?.startsWith('http') ? (
                <img src={app.icon} alt={app.name} className="w-6 h-6 object-contain" />
              ) : (
                <CalendarOutlined className="text-blue-600 text-xl" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{app.name}</h2>
              <p className="text-sm text-gray-500 mt-1 capitalize">{app.category}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <CloseOutlined className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {connectionStatus === 'connected' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircleOutlined className="text-emerald-600 text-xl" />
                <span className="text-sm font-medium text-emerald-800">Successfully Connected</span>
              </div>
              <p className="text-xs text-emerald-600 mt-1 ml-7">
                Your Google Calendar is now integrated with GainWix
              </p>
            </div>
          )}

          {connectionStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <ExclamationCircleOutlined className="text-red-600 text-xl" />
                <span className="text-sm font-medium text-red-800">Connection Failed</span>
              </div>
              {errorMessage && <p className="text-xs text-red-600 mt-1 ml-7">{errorMessage}</p>}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">About this integration</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{app.description}</p>
          </div>

          {Array.isArray(app.features) && app.features.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Features</h3>
              <div className="space-y-2">
                {app.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircleOutlined className="text-emerald-600 flex-shrink-0 text-base" />
                    <span className="text-sm text-gray-700 capitalize">
                      {String(feature).replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {connectionStatus === 'disconnected' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Permissions Required</h4>
              <p className="text-xs text-blue-700">
                By connecting, you'll authorize GainWix to access your {app.name}.
                You can revoke access at any time from your account settings.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-3">
          {connectionStatus === 'connected' ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleDisconnect}
                className="px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Disconnect
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={isConnecting}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isConnecting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    {app.icon?.startsWith('http') && <img src={app.icon} alt="" className="w-5 h-5" />}
                    Connect with {app.name?.split(' ')[0] || 'Google'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarIntegration;
